/**
 * Base service class for CRUD operations
 * Provides common functionality for all services
 */

import { BaseNode } from '@/types'
import { getAll, setAll, simulateNetworkDelay } from './storage'

export class BaseService<T extends BaseNode> {
  constructor(protected storageKey: string) {}

  /**
   * Get all
   */
  async getAll(): Promise<T[]> {
    await simulateNetworkDelay()
    return getAll<T>(this.storageKey)
  }

  /**
   * Get by ID
   */
  async getById(id: string): Promise<T | null> {
    await simulateNetworkDelay()
    const items = await this.getAll()
    return items.find((item) => item.id === id) || null
  }

  /**
   * Get multiple by IDs
   */
  async getByIds(ids: string[]): Promise<T[]> {
    await simulateNetworkDelay()
    const items = await this.getAll()
    return items.filter((item) => ids.includes(item.id))
  }

  /**
   * Create new
   */
  async create(data: Omit<T, 'id' | 'createdAt' | 'updatedAt'>): Promise<T> {
    await simulateNetworkDelay()

    const now = new Date()
    const newItem: T = {
      ...data,
      id: this.generateId(),
      createdAt: now,
      updatedAt: now,
    } as T

    const items = await this.getAll()
    items.push(newItem)
    setAll(this.storageKey, items)

    return newItem
  }

  /**
   * Update existing
   */
  async update(id: string, data: Partial<Omit<T, 'id' | 'createdAt' | 'updatedAt'>>): Promise<T> {
    await simulateNetworkDelay()

    const items = await this.getAll()
    const index = items.findIndex((item) => item.id === id)

    if (index === -1) {
      throw new Error(`Node not found: ${id}`)
    }

    const updatedItem: T = {
      ...items[index],
      ...data,
      updatedAt: new Date(),
    }

    items[index] = updatedItem
    setAll(this.storageKey, items)

    return updatedItem
  }

  /**
   * Delete
   */
  async delete(id: string): Promise<void> {
    await simulateNetworkDelay()

    const items = await this.getAll()
    const filtered = items.filter((item) => item.id !== id)

    if (filtered.length === items.length) {
      throw new Error(`Node not found: ${id}`)
    }

    setAll(this.storageKey, filtered)
  }

  /**
   * Delete multiple
   */
  async deleteMany(ids: string[]): Promise<void> {
    await simulateNetworkDelay()

    const items = await this.getAll()
    const filtered = items.filter((item) => !ids.includes(item.id))
    setAll(this.storageKey, filtered)
  }

  /**
   * Check if exists
   */
  async exists(id: string): Promise<boolean> {
    const item = await this.getById(id)
    return item !== null
  }

  /**
   * Count all
   */
  async count(): Promise<number> {
    const items = await this.getAll()
    return items.length
  }

  /**
   * Filter by predicate
   */
  async filter(predicate: (item: T) => boolean): Promise<T[]> {
    await simulateNetworkDelay()
    const items = await this.getAll()
    return items.filter(predicate)
  }

  /**
   * Find single by predicate
   */
  async findOne(predicate: (item: T) => boolean): Promise<T | null> {
    await simulateNetworkDelay()
    const items = await this.getAll()
    return items.find(predicate) || null
  }

  /**
   * Sort
   */
  async sort(compareFn: (a: T, b: T) => number): Promise<T[]> {
    const items = await this.getAll()
    return [...items].sort(compareFn)
  }

  /**
   * Paginate
   */
  async paginate(
    page: number = 1,
    pageSize: number = 10
  ): Promise<{
    data: T[]
    page: number
    pageSize: number
    totalPages: number
    totalItems: number
  }> {
    const items = await this.getAll()
    const totalItems = items.length
    const totalPages = Math.ceil(totalItems / pageSize)
    const start = (page - 1) * pageSize
    const end = start + pageSize
    const data = items.slice(start, end)

    return {
      data,
      page,
      pageSize,
      totalPages,
      totalItems,
    }
  }

  /**
   * Search by text
   */
  async search(query: string, fields: (keyof T)[]): Promise<T[]> {
    await simulateNetworkDelay()
    const items = await this.getAll()
    const lowerQuery = query.toLowerCase()

    return items.filter((item) =>
      fields.some((field) => {
        const value = item[field]
        return value && String(value).toLowerCase().includes(lowerQuery)
      })
    )
  }

  /**
   * Generate unique ID
   */
  protected generateId(): string {
    return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }

  /**
   * Clear all (for testing)
   */
  async clear(): Promise<void> {
    setAll(this.storageKey, [])
  }
}
